import React, { useState } from "react";
import { useMarkedItems } from "../lib/markedItems";
import { dimensionLabel } from "../lib/scoring";

/**
 * Slide-over panel showing all marked items in creation order, with the
 * consultant's note inline and a delete affordance. Mirrors the page-5
 * structure of the PDF so the consultant can see what they'll get.
 *
 * Triggered by the "Marked (N)" badge in the deployment header.
 */
export default function MarkedItemsTray() {
  const { marks, isOpen, close, updateNote, remove } = useMarkedItems();
  if (!isOpen) return null;
  return (
    <>
      <div
        onClick={close}
        className="fixed inset-0 z-40"
        style={{ background: "rgba(0,0,0,0.25)" }}
        data-testid="marked-tray-backdrop"
      />
      <aside
        className="fixed right-0 top-0 bottom-0 z-50 flex flex-col"
        style={{
          width: "min(540px, 100vw)",
          background: "#FBFAF7",
          boxShadow: "-2px 0 18px rgba(0,0,0,0.12)",
        }}
        role="dialog"
        aria-label="Marked items tray"
        data-testid="marked-tray"
      >
        <header className="px-7 py-5 border-b border-gray-100 flex items-center justify-between" style={{ flexShrink: 0 }}>
          <div>
            <div className="eyebrow" style={{ color: "#2F6B7C" }}>For this conversation</div>
            <h2 className="text-lg" style={{ fontFamily: "Inter", fontWeight: 500 }}>
              What you&rsquo;ve marked for the report.
            </h2>
          </div>
          <button
            onClick={close}
            data-testid="marked-tray-close"
            aria-label="Close"
            style={{
              fontFamily: "Oswald",
              fontSize: "22px",
              border: "1px solid #DDDDDD",
              background: "transparent",
              color: "#555",
              width: "32px",
              height: "32px",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-7 py-6">
          {marks.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-6" data-testid="marked-tray-list">
              {marks.map((m) => (
                <MarkRow key={m.id} mark={m} onSaveNote={updateNote} onRemove={remove} />
              ))}
            </ul>
          )}
        </div>
        <footer className="px-7 py-4 border-t border-gray-100 text-xs text-gray-500" style={{ flexShrink: 0 }}>
          These appear on page 5 of the PDF — *What this conversation should land on*.
          The notes you write here are what the facilitator reads first.
        </footer>
      </aside>
    </>
  );
}

function EmptyState() {
  return (
    <div data-testid="marked-tray-empty">
      <div className="eyebrow mb-2" style={{ color: "#999" }}>Nothing marked yet</div>
      <p className="text-sm text-gray-700 max-w-prose">
        While reading the Divergence, Compare, and Drill views, click <strong>+ Mark</strong>{" "}
        on any item or comparison worth talking about. The mark stays here until you remove it,
        and lands on page 5 of the next PDF you generate.
      </p>
    </div>
  );
}

function MarkRow({ mark, onSaveNote, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(mark.consultant_note || "");
  const [saving, setSaving] = useState(false);

  let ref;
  let stmt;
  if (mark.kind === "item") {
    const e = mark.enriched || {};
    ref = (
      <>
        <span style={{ color: "#2F6B7C" }}>{dimensionLabel(e.dimension)}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span style={{ color: "#777" }}>#{e.item_number}</span>
      </>
    );
    stmt = `"${e.text || ""}"`;
  } else if (mark.kind === "dimension_pair") {
    const p = mark.payload;
    const a = p.segment_a;
    const b = p.segment_b;
    const aStr = a.key === "_all" ? "All respondents" : `${a.key}: ${a.value}`;
    const bStr = b.key === "_all" ? "All respondents" : `${b.key}: ${b.value}`;
    ref = (
      <>
        <span style={{ color: "#2F6B7C" }}>{dimensionLabel(p.dimension)}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span style={{ color: "#777" }}>comparison</span>
      </>
    );
    stmt = (
      <>
        {aStr}
        <span className="mx-2 text-gray-400" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", fontSize: "9px" }}>vs</span>
        {bStr}
      </>
    );
  } else if (mark.kind === "trend_delta") {
    const dim = mark.payload?.dimension;
    ref = (
      <>
        <span style={{ color: "#2F6B7C" }}>{dimensionLabel(dim)}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span style={{ color: "#777" }}>trend</span>
      </>
    );
    stmt = (
      <span style={{ color: "#555" }}>
        Δ across waves — values resolve at the time the report is generated.
      </span>
    );
  } else if (mark.kind === "benchmark_placement") {
    const dim = mark.payload?.dimension;
    ref = (
      <>
        <span style={{ color: "#2F6B7C" }}>{dimensionLabel(dim)}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span style={{ color: "#777" }}>benchmark</span>
      </>
    );
    stmt = (
      <span style={{ color: "#555" }}>
        Corpus placement — values resolve at the time the report is generated.
      </span>
    );
  } else {
    return null;
  }

  async function save() {
    setSaving(true);
    try {
      await onSaveNote(mark.id, note.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="border-b border-gray-100 pb-5" data-testid={`marked-tray-row-${mark.id}`}>
      <div className="text-xs mb-1" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase" }}>
        {ref}
      </div>
      <div
        className="text-sm text-ink mb-3"
        style={{ fontFamily: mark.kind === "item" ? "Source Serif 4, Georgia, serif" : "Inter", fontStyle: mark.kind === "item" ? "italic" : "normal", lineHeight: 1.45 }}
      >
        {stmt}
      </div>
      {editing ? (
        <div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            className="field w-full text-sm"
            style={{ fontFamily: "Inter", lineHeight: 1.5 }}
            data-testid={`marked-tray-row-${mark.id}-note-input`}
          />
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => { setEditing(false); setNote(mark.consultant_note || ""); }}
              className="text-xs"
              style={{ fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase", background: "transparent", border: 0, color: "#555", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="text-xs px-3 py-1"
              style={{ fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase", background: "#1A1A1A", color: "#FBFAF7", border: 0, cursor: saving ? "wait" : "pointer" }}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          {mark.consultant_note ? (
            <p
              className="text-sm text-gray-700"
              style={{ fontFamily: "Inter", lineHeight: 1.55, borderLeft: "3px solid #AD393E", paddingLeft: "10px" }}
            >
              {mark.consultant_note}
            </p>
          ) : (
            <p className="text-xs text-gray-400 italic">No note yet.</p>
          )}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="text-xs"
              style={{ fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase", background: "transparent", border: 0, color: "#2F6B7C", cursor: "pointer" }}
              data-testid={`marked-tray-row-${mark.id}-edit`}
            >
              Edit
            </button>
            <button
              onClick={() => { if (window.confirm("Remove this mark?")) onRemove(mark.id); }}
              className="text-xs"
              style={{ fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase", background: "transparent", border: 0, color: "#AD393E", cursor: "pointer" }}
              data-testid={`marked-tray-row-${mark.id}-remove`}
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
